import { ChevronDown } from 'lucide-react';
import { SearchIcon } from 'assets/icons';
import StyledInput from 'components/styledInput/StyledInput';
import { useMemo, useState } from 'react';
import { filterTemplates, groupTemplatesByQualification } from './missionTemplateFormat';
import TemplateRow, { ListTag } from './TemplateRow';

// The job descriptions of one qualification, in a card of their own: a header
// that folds the group (name and count), then its rows.
const TemplateGroup = ({ group, isCollapsed, onToggle, onOpen }) => {
  const contentId = `template-group-${group.key || 'none'}`;
  return (
    <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
      <button
        type="button"
        onClick={() => onToggle(group.key)}
        aria-expanded={!isCollapsed}
        aria-controls={contentId}
        title={isCollapsed ? 'Déplier le groupe' : 'Replier le groupe'}
        className="flex w-full items-center gap-2 bg-gray-50 px-5 py-2.5 text-left hover:bg-gray-100 focus:outline-none focus-visible:bg-gray-100"
      >
        <ChevronDown
          size={16}
          color="#6B7280"
          aria-hidden="true"
          className={`shrink-0 transition-transform ${isCollapsed ? '-rotate-90' : ''}`}
        />
        <span className="truncate text-xs font-medium leading-4 text-gray-700">{group.qualification || 'Sans qualification'}</span>
        <ListTag>{group.templates.length}</ListTag>
      </button>
      {!isCollapsed ? (
        <div id={contentId} className="border-t border-gray-100">
          {group.templates.map((template) => (
            <TemplateRow key={template._id} template={template} onOpen={onOpen} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

/**
 * The job descriptions of a company with their search, as the company front
 * lists them: one card when they share a qualification, else one folding card
 * per qualification. `onOpen`: opens one (none: they are only read).
 */
const TemplateList = ({ templates, onOpen }) => {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());
  const visible = useMemo(() => filterTemplates(templates, search), [templates, search]);
  const groups = useMemo(() => groupTemplatesByQualification(visible), [visible]);
  // A search opens every group, so a match never hides in a folded one.
  const isSearching = search.trim() !== '';
  const toggle = (key) =>
    setCollapsed((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="flex flex-col gap-3">
      <div className="w-[350px] max-w-full">
        <StyledInput
          type="text"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Rechercher une fiche de poste"
          imgFront={<SearchIcon wh={20} color="#9CA3AF" />}
        />
      </div>
      {!visible.length && (
        <p className="py-12 text-center text-sm text-gray-500" role="status">
          Aucune fiche de poste ne correspond à votre recherche.
        </p>
      )}
      {visible.length > 0 && groups.length <= 1 && (
        <div className="overflow-hidden rounded-lg border border-gray-100 bg-white">
          {visible.map((template) => (
            <TemplateRow key={template._id} template={template} onOpen={onOpen} />
          ))}
        </div>
      )}
      {groups.length > 1 &&
        groups.map((group) => (
          <TemplateGroup
            key={group.key || 'none'}
            group={group}
            isCollapsed={!isSearching && collapsed.has(group.key)}
            onToggle={toggle}
            onOpen={onOpen}
          />
        ))}
    </div>
  );
};

export default TemplateList;
